import React, { useState, useMemo } from 'react';
import styles from '../../styles/GeneracionCargosAutomaticosStyles/GeneracionCargosAutomaticos.module.css';

function EstudiantesPreviewTable({ estudiantes, stats, onDownloadCSV, conceptoAplicaBecas }) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Filtrar duplicados antes de paginar
  const estudiantesElegibles = estudiantes.filter(e => e.estado !== 'DUPLICADO');
  const totalPages = Math.ceil(estudiantesElegibles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const estudiantesActuales = estudiantesElegibles.slice(startIndex, endIndex);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Formatear número como moneda
  const formatMonto = (monto) => {
    return `$${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Validación de datos incompletos
  const validacionDatos = useMemo(() => {
    const elegibles = estudiantes.filter(e => e.estado !== 'DUPLICADO');
    const conDatosIncompletos = elegibles.filter(e => 
      !e.id_estudiante || !e.matricula || e.monto_final === null || e.monto_final === undefined
    );
    const conMontoNegativo = elegibles.filter(e => e.monto_final < 0);
    const conMontoInvalido = elegibles.filter(e => isNaN(e.monto_final));
    
    return {
      elegibles: elegibles.length,
      datosIncompletos: conDatosIncompletos.length,
      montoNegativo: conMontoNegativo.length,
      montoInvalido: conMontoInvalido.length,
      hayProblemas: conDatosIncompletos.length > 0 || conMontoNegativo.length > 0 || conMontoInvalido.length > 0
    };
  }, [estudiantes]);

  return (
    <div className={styles.previewTableContainer}>
      <div className={styles.previewTableHeader}>
        <div className={styles.previewTableTitle}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
          </svg>
          <h3>Estudiantes Elegibles ({stats.total})</h3>
        </div>
        
        <div className={styles.previewStats}>
          <span className={styles.statBadge}>
            <span className={styles.statLabel}>Elegibles:</span>
            <span className={styles.statValue}>{estudiantes.filter(e => e.estado === 'ELEGIBLE').length}</span>
          </span>
          {stats.conBeca > 0 && (
            <span className={`${styles.statBadge} ${styles.statSuccess}`}>
              <span className={styles.statLabel}>Con Beca:</span>
              <span className={styles.statValue}>{stats.conBeca}</span>
            </span>
          )}
          {stats.duplicados > 0 && (
            <span className={`${styles.statBadge} ${styles.statWarning}`}>
              <span className={styles.statLabel}>Duplicados excluidos:</span>
              <span className={styles.statValue}>{stats.duplicados}</span>
            </span>
          )}
        </div>
      </div>
      
      {/* Alerta de validación de datos */}
      {validacionDatos.hayProblemas && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <svg viewBox="0 0 24 24" fill="#dc2626" width="20" height="20" style={{flexShrink: 0, marginTop: '2px'}}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <div>
            <div style={{fontWeight: '600', color: '#991b1b', marginBottom: '4px'}}>
              Problemas de validación detectados
            </div>
            <div style={{fontSize: '13px', color: '#7f1d1d'}}>
              {validacionDatos.datosIncompletos > 0 && (
                <div>- {validacionDatos.datosIncompletos} estudiante(s) con datos incompletos</div>
              )}
              {validacionDatos.montoNegativo > 0 && (
                <div>- {validacionDatos.montoNegativo} estudiante(s) con monto negativo</div>
              )}
              {validacionDatos.montoInvalido > 0 && (
                <div>- {validacionDatos.montoInvalido} estudiante(s) con monto inválido</div>
              )}
              <div style={{marginTop: '8px', fontStyle: 'italic'}}>
                Por favor revise los datos antes de generar los cargos.
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Resumen de montos con becas */}
      {conceptoAplicaBecas && stats.ahorroPorBecas > 0 && (
        <div className={styles.becaSummaryBox}>
          <div className={styles.becaSummaryIcon}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
            </svg>
          </div>
          <div className={styles.becaSummaryContent}>
            <span className={styles.becaSummaryTitle}>Descuento por Becas Aplicado</span>
            <div className={styles.becaSummaryValues}>
              <span>Monto Original: <strong>{formatMonto(stats.montoOriginal)}</strong></span>
              <span className={styles.becaAhorro}>Ahorro: <strong>-{formatMonto(stats.ahorroPorBecas)}</strong></span>
              <span>Monto Final: <strong className={styles.montoFinal}>{formatMonto(stats.montoFinal)}</strong></span>
            </div>
          </div>
        </div>
      )}
      
      <div className={styles.tableWrapper}>
        <table className={styles.previewTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>Matrícula</th>
              <th>Nombre Completo</th>
              <th>Programa</th>
              <th>Sección</th>
              {conceptoAplicaBecas && <th>Beca</th>}
              <th>Monto Original</th>
              {conceptoAplicaBecas && <th>Descuento</th>}
              <th>Monto Final</th>
              <th>Estatus</th>
            </tr>
          </thead>
          <tbody>
            {estudiantesActuales.map((estudiante, index) => (
              <tr key={estudiante.id_estudiante || index} className={estudiante.estado === 'DUPLICADO' ? styles.duplicadoRow : ''}>
                <td>{startIndex + index + 1}</td>
                <td>{estudiante.matricula}</td>
                <td>{estudiante.nombre}</td>
                <td>{estudiante.programa || '-'}</td>
                <td>{estudiante.seccion || '-'}</td>
                {conceptoAplicaBecas && (
                  <td>
                    {estudiante.tiene_beca ? (
                      <div className={styles.becaIndicator} title={estudiante.becas?.map(b => `${b.nombre} (${b.tipo === 'MONTO' ? formatMonto(b.monto_asignado) : `${b.porcentaje}%`})`).join(', ') || 'Beca activa'}>
                        <svg viewBox="0 0 24 24" fill="currentColor" className={styles.becaIcon}>
                          <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                        </svg>
                        <span className={styles.becaCount}>{estudiante.becas?.length || 1}</span>
                      </div>
                    ) : (
                      <span className={styles.noBeca}>-</span>
                    )}
                  </td>
                )}
                <td>{formatMonto(estudiante.monto_original || estudiante.monto)}</td>
                {conceptoAplicaBecas && (
                  <td className={estudiante.descuento_beca > 0 ? styles.descuentoCell : ''}>
                    {estudiante.descuento_beca > 0 ? (
                      <span className={styles.descuentoValue}>-{formatMonto(estudiante.descuento_beca)}</span>
                    ) : '-'}
                  </td>
                )}
                <td className={styles.montoFinalCell}>
                  <strong>{formatMonto(estudiante.monto_final || estudiante.monto)}</strong>
                </td>
                <td>
                  {estudiante.estado === 'DUPLICADO' ? (
                    <span className={`${styles.statusBadge} ${styles.statusDuplicated}`}>
                      Duplicado
                    </span>
                  ) : (
                    <span className={`${styles.statusBadge} ${styles.statusActive}`}>
                      Activo
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Paginación */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button 
            className={styles.pageButton}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            &laquo; Anterior
          </button>
          
          <div className={styles.pageNumbers}>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                if (totalPages <= 5) return true;
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - currentPage) <= 1) return true;
                return false;
              })
              .map((page, index, array) => (
                <React.Fragment key={page}>
                  {index > 0 && array[index - 1] !== page - 1 && (
                    <span className={styles.pageEllipsis}>...</span>
                  )}
                  <button
                    className={`${styles.pageNumber} ${currentPage === page ? styles.pageActive : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                </React.Fragment>
              ))
            }
          </div>
          
          <button 
            className={styles.pageButton}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Siguiente &raquo;
          </button>
        </div>
      )}
      
      {/* Botón de descarga en la tabla */}
      <div className={styles.previewTableFooter}>
        <button className={styles.csvButtonAlt} onClick={onDownloadCSV}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Descargar CSV con todos los estudiantes
        </button>
        <span className={styles.footerNote}>
          Se recomienda descargar el CSV antes de aplicar los cargos como respaldo
        </span>
      </div>
    </div>
  );
}

export default EstudiantesPreviewTable;
